import type { SelectedOptions } from '@corratech/pylot-variant-selector'
import type { UseOptionSelectorReturn } from '@corratech/pylot-variant-selector/src/hooks/useOptionSelector'
import type { Option } from '@pylot-data/hooks/product/use-product-ui'
import { OptionType } from '@pylot-data/hooks/product/use-product-ui'
import {
    COLOR_SWATCH_DATA,
    IMAGE_SWATCH_DATA
} from '@corratech/pylot-variant-selector/src/hooks/useOptionSelector/utils/getOptionType'
import { useTranslation } from 'next-i18next'
import type { ReactElement } from 'react'
import React from 'react'
import Swatch from '../Swatch/Swatch'
import s from '@corratech/pylot-swatches/src/SwatchGroup/SwatchGroup.module.scss'
import cn from 'classnames'
import {
    ConfigurableProductOptionsValues,
    Maybe,
    ImageSwatchData,
    ColorSwatchData,
    TextSwatchData
} from '@pylot-data/pylotschema'
import { ViewMore } from '@components/ViewMoreSwatch/ViewMore'
import { sortAlphabetically } from '@components/alfakher/pylot-utils'

type SwatchGroupProps = Omit<UseOptionSelectorReturn, 'options'> & {
    option: Option
    isCircle?: boolean
    disableAll?: boolean
    displaySwatchOptionLabel?: boolean
    disabledValues?: string[]
    viewMore: boolean
}

type ConfigurableProductOptions = ConfigurableProductOptionsValues & {
    swatch_data?: Maybe<ImageSwatchData | ColorSwatchData | TextSwatchData>
}

export const SwatchGroup = ({
    option,
    selectedOptions,
    isCircle = true,
    disableAll = false,
    displaySwatchOptionLabel = false,
    disabledValues = [],
    setSelectedOptions,
    viewMore
}: SwatchGroupProps): ReactElement => {
    const { t } = useTranslation('common')
    const active = selectedOptions[option.code as keyof SelectedOptions] ?? ''

    if (option.type === OptionType.Dropdown) {
        const sortedValues = option.values?.sort((a, b) =>
            sortAlphabetically(a.label, b.label)
        )

        return (
            <div className={cn('swatch-group-wrap', s['swatch-group'])}>
                {/* TODO: update jsx-a11y? As right now the rule is deprecated.
                    According to the provided link https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-onchange.md :
                    ⚠️ Deprecated: This rule is based on reports of behavior of old browsers (eg. IE 10 and below).
                    In the meantime, this behavior has been corrected, both in newer versions of browsers as well as in the DOM spec.
                 */}
                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                <select
                    className="form-select block appearance-none w-full bg-grey-lighter border-2 border-grey-lighter text-p-1 py-3 px-4 pr-8 rounded focus:shadow-none focus:border-accents-2 max-w-sm"
                    id="grid-state"
                    value={active}
                    onChange={(e) => {
                        e.persist()
                        setSelectedOptions((oldSelectedOptions) => {
                            return {
                                ...oldSelectedOptions,
                                [option.code]: e.target?.value ?? ''
                            }
                        })
                    }}
                    // onBlur={() => {}}
                    // onFocus={() => {}}
                >
                    <option selected={active === ''} value="">
                        {t('Select an option...')}
                    </option>
                    {sortedValues?.map((v, i: number) => {
                        const value = v.label!
                        const OutOfstockValue: boolean = disabledValues.includes(
                            value
                        )
                        if (!OutOfstockValue) {
                            return (
                                <option
                                    key={`${v.value_index ?? value}-${i}`}
                                    value={value}
                                >
                                    {value}
                                </option>
                            )
                        }
                    })}
                </select>
            </div>
        )
    }

    const swatchContent = (
        <div className={cn('swatch-group-wrap', s['swatch-group'])}>
            {option.values!.map((v: ConfigurableProductOptions, i: number) => {
                const color =
                    v.swatch_data?.__typename === COLOR_SWATCH_DATA
                        ? v.swatch_data?.value
                        : ''
                const url =
                    v.swatch_data?.__typename === IMAGE_SWATCH_DATA
                        ? v.swatch_data?.thumbnail
                        : ''

                const value = v.label!
                const OutOfstockValue: boolean = disabledValues.includes(value)
                if (!OutOfstockValue) {
                    return (
                        <Swatch
                            key={`${v.value_index}-${i}`}
                            active={value === active}
                            color={color!}
                            label={value}
                            displaySwatchOptionLabel={displaySwatchOptionLabel}
                            optionType={option.type}
                            isCircle={isCircle}
                            url={url!}
                            onClick={() => {
                                setSelectedOptions((oldSelectedOptions) => {
                                    const newValue =
                                        option.type === OptionType.TextSwatch
                                            ? v.swatch_data?.value ?? value
                                            : value

                                    return {
                                        ...oldSelectedOptions,
                                        [option.code]: newValue!
                                    }
                                })
                            }}
                        />
                    )
                }
            })}
        </div>
    )

    if (option.type === 'TextSwatch' && viewMore) {
        return <ViewMore>{swatchContent}</ViewMore>
    }

    return swatchContent
}
